import { forwardRef, useCallback, useMemo } from 'react';
import Link from 'next/link';

import { SparksAgentService } from '@/components/composite/SparksAgent/services/SparksAgentService';

import type { GlobalAiQuota } from '@/utils/hooks/swr/quotas/useGlobalAiQuota';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useUserRole from '@/utils/hooks/user/useUserRole';

import { classNames } from '@/utils/classNames';

import { SUBSCRIPTION } from '@/lib/routes/privateRoutes';

import Icon from '@/components/base/Icon';
import { Trans } from '@/components/tools/translation';

type SparksInputProps = {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    disabled?: boolean;
    aiQuota?: GlobalAiQuota;
    isQuotaLoading?: boolean;
};

export const SparksInput = forwardRef<HTMLTextAreaElement, SparksInputProps>(
    ({ value, onChange, onSend, disabled = false, aiQuota, isQuotaLoading = false }, ref) => {
        const { t, lang } = useTranslation('sparks');
        const { isAdmin } = useUserRole();

        const { isQuotaExhausted, showRemainingQuota } = SparksAgentService.getGlobalAiQuotaDerived(
            aiQuota,
            isQuotaLoading
        );

        const { remainingLabel, totalLabel } = useMemo(() => {
            if (!showRemainingQuota || aiQuota?.total === null || aiQuota?.remaining === null) {
                return { remainingLabel: '', totalLabel: '' };
            }
            return {
                remainingLabel: SparksAgentService.formatCompactAiAmount(aiQuota.remaining, lang),
                totalLabel: SparksAgentService.formatCompactAiAmount(aiQuota.total, lang)
            };
        }, [aiQuota?.remaining, aiQuota?.total, lang, showRemainingQuota]);

        const inputDisabled = disabled || isQuotaExhausted;
        const canSend = value.trim().length > 0 && !inputDisabled;

        const handleSend = () => onSend();

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === 'Enter' && !e.shiftKey && canSend) {
                    e.preventDefault();
                    handleSend();
                }
            },
            [canSend, onSend]
        );

        return (
            <div className="shrink-0 px-3.5">
                <div className="flex flex-col gap-1 overflow-hidden rounded-lg border border-[#DBE6EA] bg-white px-1 pb-3 pt-1 shadow-icoXS dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
                    {isQuotaExhausted ? (
                        <div className="flex items-start gap-1.5 rounded bg-[#F4F8F9] p-2.5 dark:bg-icoDarkMode-deepBlack">
                            <Icon
                                icon="arrow-top"
                                className="flex size-3 shrink-0 items-center justify-center rounded-full bg-icoBlue text-[8px] leading-none text-white before:-mb-px before:-ml-px"
                            />
                            <div className="min-w-0 flex-1 font-GMedium text-[10px] leading-3 tracking-[-0.03px] text-icoGray-500 dark:text-icoDarkMode-wolf">
                                {isAdmin ? (
                                    <Trans
                                        i18nKey="sparks:quota_exhausted_banner_admin"
                                        components={{
                                            upgrade: (
                                                <Link
                                                    href={SUBSCRIPTION}
                                                    className="font-GSemiBold text-icoBlue hover:underline"
                                                />
                                            )
                                        }}
                                    />
                                ) : (
                                    t('quota_exhausted_banner_member')
                                )}
                            </div>
                        </div>
                    ) : null}
                    <div className="flex flex-col px-2">
                        <textarea
                            ref={ref}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('input_placeholder')}
                            rows={2}
                            disabled={inputDisabled}
                            className={classNames(
                                'w-full resize-none border-none bg-transparent py-2 pl-0 pr-2 font-GMedium text-xs tracking-[-0.072px] outline-none ring-0 focus:outline-none focus:ring-0',
                                'text-icoBlack placeholder:text-icoGray-400 dark:text-white dark:placeholder:text-icoDarkMode-wolf',
                                inputDisabled && 'cursor-not-allowed opacity-60'
                            )}
                        />

                        <div className="flex items-baseline justify-between">
                            {showRemainingQuota ? (
                                <p className="font-GMedium text-[10px] leading-3 tracking-tight text-icoGray-400 dark:text-icoDarkMode-wolf">
                                    {t('quota_remaining', {
                                        remaining: remainingLabel,
                                        total: totalLabel
                                    })}
                                </p>
                            ) : null}

                            <div className="flex flex-1 items-center justify-end">
                                {/* Coming soon (v2) */}
                                {/* <button
                                    type="button"
                                    className="flex items-center justify-center text-icoGray-500 transition-colors hover:text-icoBlack">
                                    <Icon icon="plus" className="text-[14px]" />
                                </button> */}
                                <button
                                    type="button"
                                    onClick={handleSend}
                                    disabled={!canSend}
                                    className={classNames(
                                        'flex size-6 items-center justify-center rounded-full transition-colors',
                                        !canSend && 'p-0.5',
                                        canSend
                                            ? 'bg-icoBlue text-white dark:bg-icoBlue'
                                            : 'cursor-not-allowed bg-icoGray-200 text-icoGray-500 dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf'
                                    )}>
                                    <Icon
                                        icon="arrow-top"
                                        className={classNames(
                                            'text-[14px]',
                                            !canSend && 'opacity-30'
                                        )}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
);

SparksInput.displayName = 'SparksInput';
